import {inject, type InjectionKey, provide, type Ref, ComputedRef} from "vue";
import type {Categories} from "@t/list/Category";
import type { DisplayRule, FormLayout } from "@t/fd/layout/FormLayout";
import type {FormPart} from "@t/fd/FormPart";
import type {FormPartOrReference} from "@t/fd/FormPartOrReference";
import type {FormList} from "@t/fd/layout/FormDefinition";
import type {FormEnvironment} from "./Types";
import type {LibraryFunctions} from "@t/fd/Lib";
import type {ApiError} from "@t/util/ApiError";
import {InterfaceRoot, RootLayout} from "@t/fd/InterfaceRoot.ts";
export { contextKey } from "../types/fd/AppContext.ts";

export interface FindListFunc {
	(id: string, ...values: any): Ref<FormList>;
}

export interface LoadListFunc {
    async (id: string): FormList
}

export interface FindCategoryFunc {
	(id: string, ...values: any): Ref<Categories>;
}
export interface ExecuteFunc {
    (funcName: string, localData: object, localPart: FormPart, ...extraArgs: any): any;
}

export interface PartRefFunc {
    (pr: FormPartOrReference): FormPart
}

export interface ComponentOfPartFunc {
    (pr: FormPartOrReference): any
}

export type ErrorMap = ApiError[];


export const provideWidgetDisplayKey = Symbol() as InjectionKey<DisplayRule>;
export const findListKey = Symbol() as InjectionKey<FindListFunc>;
export const loadListKey = Symbol() as InjectionKey<LoadListFunc>;
export const findCategoryKey = Symbol() as InjectionKey<FindCategoryFunc>;
export const executeKey = Symbol() as InjectionKey<ExecuteFunc>;
export const partRefKey = Symbol() as InjectionKey<PartRefFunc>;
export const componentOfPartKey = Symbol() as InjectionKey<ComponentOfPartFunc>;
export const interfaceRootKey = Symbol() as InjectionKey<ComputedRef<InterfaceRoot<RootLayout>>>;
export const formDataKey = Symbol() as InjectionKey<object | any[]>;
export const libKey = Symbol() as InjectionKey<LibraryFunctions>;
export const pathKey = Symbol() as InjectionKey<string>;
export const errorMapKey = Symbol() as InjectionKey<ErrorMap>;
export const parentOrgKey = Symbol() as InjectionKey<ComputedRef<boolean>>;

export function defaultWidgetDisplay(env:FormEnvironment):DisplayRule {
    return env.touch ?
        {
            colon: false,
            label: "lowercase",
            input: "stacked",
            area: "stacked",
            option: "checkbox-pull",
            switch: "spaced",
        }
        :
        {
            colon: true,
            label: "none",
            input: "standard",
            area: "stacked",
            option: "checkbox-push",
            switch: "spaced",
        };
}

/** If there is a new display rule it will be returned. If the current rule is inherited this method will return null */
export function provideWidgetDisplay(part: FormLayout, env:FormEnvironment): DisplayRule|null {
	if (part.widgetDisplay) {
        let current = inject(provideWidgetDisplayKey, null);
        if (!current) current = defaultWidgetDisplay(env);
        // Merge rules
        const wd = {...current, ...part.widgetDisplay};
		provide(provideWidgetDisplayKey, wd);
		return wd;
	}
	return null;
}

export function injectWidgetDisplay(env:FormEnvironment): DisplayRule {
	const dr = inject(provideWidgetDisplayKey, defaultWidgetDisplay(env));
	return dr;
}

export function injectFindList():FindListFunc {
    let findList = inject(findListKey);
    if (typeof findList === "undefined") throw "Undefined findList (not provided)";
    return findList;
}

// TODO Replace with env.loadList
export function injectLoadList():LoadListFunc {
    let loadList = inject(loadListKey);
    if (typeof loadList === "undefined") throw "Undefined loadList (not provided)";
    return loadList;
}

export function injectFindCategory():FindCategoryFunc {
    let findCategory = inject(findCategoryKey);
    if (typeof findCategory === "undefined") throw "Undefined findCategory (not provided)";
    return findCategory;
}

export function injectExecute():ExecuteFunc {
    let ex = inject(executeKey);
    if (typeof ex === "undefined") throw "Undefined execute method (not provided)";
    return ex;
}

export function injectPartRef():PartRefFunc {
    let ex = inject(partRefKey);
    if (typeof ex === "undefined") throw "Undefined partRef method (not provided)";
    return ex;
}

export function injectComponentOfPart():ComponentOfPartFunc {
    let ex = inject(componentOfPartKey);
    if (typeof ex === "undefined") throw "Undefined componentOfPart method (not provided)";
    return ex;
}

export function injectInterfaceRoot():ComputedRef<InterfaceRoot<RootLayout>> {
    let ex = inject(interfaceRootKey);
    if (typeof ex === "undefined") throw "Undefined InterfaceRoot (not provided)";
    return ex;
}

export function injectFormData():any {
    let ex = inject(formDataKey);
    if (typeof ex === "undefined") {
		console.trace("injectFormData");
		throw "Undefined root data (not provided)";
	}
    return ex;
}

export function provideLib(lib: LibraryFunctions):LibraryFunctions {
    provide(libKey, lib);
    return lib;
}
export function injectLib():LibraryFunctions {
    let ex = inject(libKey);
    if (typeof ex === "undefined") throw "Undefined lib (not provided)";
    return ex;
}

export function injectErrors():ErrorMap {
    return inject(errorMapKey, []);
}
